/**
 * Gallery theme helpers for easier migration from old galleryTheme module
 * These functions provide the same API but use the new theme structure
 */
import type { DefaultTheme } from 'styled-components';

// Theme prop interface for styled-components
interface ThemeProps {
  theme: DefaultTheme;
}

// Color helpers
export const galleryColors = {
  white: ({ theme }: ThemeProps) => theme.colors.gallery.white,
  surface: ({ theme }: ThemeProps) => theme.colors.gallery.surface,
  surfaceRaised: ({ theme }: ThemeProps) => theme.colors.gallery.surfaceRaised,
  surfaceElevated: ({ theme }: ThemeProps) => theme.colors.gallery.surfaceElevated,
  mat: ({ theme }: ThemeProps) => theme.colors.gallery.mat,
  card: ({ theme }: ThemeProps) => theme.colors.gallery.card,
  secondary: ({ theme }: ThemeProps) => theme.colors.gallery.secondary,
  charcoal: ({ theme }: ThemeProps) => theme.colors.gallery.charcoal,
  stormGrey: ({ theme }: ThemeProps) => theme.colors.gallery.stormGrey,
  silver: ({ theme }: ThemeProps) => theme.colors.gallery.silver,
  softWhite: ({ theme }: ThemeProps) => theme.colors.gallery.neutrals.lightest,

  // Neutrals
  neutralLightest: ({ theme }: ThemeProps) => theme.colors.gallery.neutrals.lightest,
  neutralLight: ({ theme }: ThemeProps) => theme.colors.gallery.neutrals.light,
  neutralMedium: ({ theme }: ThemeProps) => theme.colors.gallery.neutrals.medium,
  neutralDark: ({ theme }: ThemeProps) => theme.colors.gallery.neutrals.dark,
  neutralDarkest: ({ theme }: ThemeProps) => theme.colors.gallery.neutrals.darkest,

  // Accents
  primary: ({ theme }: ThemeProps) => theme.colors.gallery.accent.primary,
  hover: ({ theme }: ThemeProps) => theme.colors.gallery.accent.hover,
  active: ({ theme }: ThemeProps) => theme.colors.gallery.accent.active,
  border: ({ theme }: ThemeProps) => theme.colors.gallery.accent.border,

  // Aliases for compatibility with galleryTheme.ts
  accent: ({ theme }: ThemeProps) => theme.colors.gallery.accent.primary,
  accentHover: ({ theme }: ThemeProps) => theme.colors.gallery.accent.hover,
};

// Spacing helpers
export const gallerySpacing = {
  micro: ({ theme }: ThemeProps) => theme.spacing.gallery.micro,
  tiny: ({ theme }: ThemeProps) => theme.spacing.gallery.tiny,
  xs: ({ theme }: ThemeProps) => theme.spacing.gallery.xs,
  sm: ({ theme }: ThemeProps) => theme.spacing.gallery.sm,
  md: ({ theme }: ThemeProps) => theme.spacing.gallery.md,
  lg: ({ theme }: ThemeProps) => theme.spacing.gallery.lg,
  xl: ({ theme }: ThemeProps) => theme.spacing.gallery.xl,
  '2xl': ({ theme }: ThemeProps) => theme.spacing.gallery['2xl'],
  '3xl': ({ theme }: ThemeProps) => theme.spacing.gallery['3xl'],
  '4xl': ({ theme }: ThemeProps) => theme.spacing.gallery['4xl'],

  // Artwork-specific spacing
  artworkTight: ({ theme }: ThemeProps) => theme.spacing.gallery.artwork.tight,
  artworkComfortable: ({ theme }: ThemeProps) => theme.spacing.gallery.artwork.comfortable,
  artworkGenerous: ({ theme }: ThemeProps) => theme.spacing.gallery.artwork.generous,

  // Layout spacing
  section: ({ theme }: ThemeProps) => theme.spacing.gallery.layout.section,
  margin: ({ theme }: ThemeProps) => theme.spacing.gallery.layout.margin,
  gutter: ({ theme }: ThemeProps) => theme.spacing.gallery.layout.gutter,
};

// Border radius helpers
export const galleryBorderRadius = {
  none: ({ theme }: ThemeProps) => theme.borderRadius.gallery.none,
  subtle: ({ theme }: ThemeProps) => theme.borderRadius.gallery.subtle,
  soft: ({ theme }: ThemeProps) => theme.borderRadius.gallery.soft,
  comfortable: ({ theme }: ThemeProps) => theme.borderRadius.gallery.comfortable,
  prominent: ({ theme }: ThemeProps) => theme.borderRadius.gallery.prominent,
  dramatic: ({ theme }: ThemeProps) => theme.borderRadius.gallery.dramatic,
};

// Shadow helpers
export const galleryShadows = {
  none: ({ theme }: ThemeProps) => theme.shadows.gallery.none,
  subtle: ({ theme }: ThemeProps) => theme.shadows.gallery.subtle,
  soft: ({ theme }: ThemeProps) => theme.shadows.gallery.soft,
  comfortable: ({ theme }: ThemeProps) => theme.shadows.gallery.comfortable,
  prominent: ({ theme }: ThemeProps) => theme.shadows.gallery.prominent,
  dramatic: ({ theme }: ThemeProps) => theme.shadows.gallery.dramatic,
  hover: ({ theme }: ThemeProps) => theme.shadows.gallery.hover,
  focus: ({ theme }: ThemeProps) => theme.shadows.gallery.focus,

  // Artwork-specific shadows
  artworkSubtle: ({ theme }: ThemeProps) => theme.shadows.gallery.artwork.subtle,
  artworkFrame: ({ theme }: ThemeProps) => theme.shadows.gallery.artwork.frame,
  artworkMuseum: ({ theme }: ThemeProps) => theme.shadows.gallery.artwork.museum,
};

// Transition helpers
export const galleryTransitions = {
  instant: ({ theme }: ThemeProps) => theme.transitions.gallery.instant,
  quick: ({ theme }: ThemeProps) => theme.transitions.gallery.quick,
  smooth: ({ theme }: ThemeProps) => theme.transitions.gallery.smooth,
  elegant: ({ theme }: ThemeProps) => theme.transitions.gallery.elegant,
  dramatic: ({ theme }: ThemeProps) => theme.transitions.gallery.dramatic,
  luxurious: ({ theme }: ThemeProps) => theme.transitions.gallery.luxurious,
  hover: ({ theme }: ThemeProps) => theme.transitions.gallery.hover,
  reveal: ({ theme }: ThemeProps) => theme.transitions.gallery.reveal,
  modal: ({ theme }: ThemeProps) => theme.transitions.gallery.modal,
};

// Gallery grid helpers
export const galleryGrids = {
  masonry: ({ theme }: ThemeProps) => `
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: ${theme.spacing.gallery.artwork.generous};
    
    @media (min-width: ${theme.breakpoints.tablet}) {
      grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    }
    
    @media (min-width: ${theme.breakpoints.desktop}) {
      grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    }
  `,

  standard: ({ theme }: ThemeProps) => `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: ${theme.spacing.gallery.md};
    
    @media (min-width: ${theme.breakpoints.tablet}) {
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    }
  `,

  // Legacy aliases for backward compatibility
  masonryGrid: ({ theme }: ThemeProps) => galleryGrids.masonry({ theme }),
  artworkGrid: ({ theme }: ThemeProps) => galleryGrids.standard({ theme }),
};

// Gallery patterns (common styling patterns)
export const galleryPatterns = {
  button: ({ theme }: ThemeProps) => `
    font-family: ${theme.typography.fontFamily.gallery.heading};
    font-weight: ${theme.typography.fontWeight.gallery.medium};
    background-color: ${theme.colors.gallery.accent.primary};
    color: ${theme.colors.gallery.text.inverse};
    border: 1px solid ${theme.colors.gallery.accent.primary};
    border-radius: ${theme.borderRadius.gallery.comfortable};
    padding: ${theme.spacing.gallery.xs} ${theme.spacing.gallery.sm};
    transition: all ${theme.transitions.gallery.hover};
    cursor: pointer;
    font-size: ${theme.typography.fontSize.gallery.caption};
    letter-spacing: 0;
    
    &:hover {
      background-color: ${theme.colors.gallery.accent.secondary};
      border-color: ${theme.colors.gallery.accent.secondary};
      color: ${theme.colors.gallery.text.inverse};
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.gallery.soft};
    }
    
    &:focus {
      outline: 2px solid ${theme.colors.gallery.accent.primary};
      outline-offset: 2px;
    }
  `,

  card: ({ theme }: ThemeProps) => `
    background-color: ${theme.colors.gallery.card};
    border: 1px solid ${theme.colors.gallery.border.subtle};
    border-radius: ${theme.borderRadius.gallery.soft};
    box-shadow: ${theme.shadows.gallery.subtle};
    transition: all ${theme.transitions.gallery.hover};
    
    &:hover {
      border-color: ${theme.colors.gallery.accent.border};
      box-shadow: ${theme.shadows.gallery.comfortable};
      transform: translateY(-2px);
    }
  `,
};
